import React, { useState, useCallback, useRef } from 'react';
import { useEstabelecimento } from '../../hooks/useEstabelecimento';

const MAX_TENTATIVAS = 3;

/**
 * Seletor de estabelecimento com dropdown.
 * Requisitos: 3.1, 3.2, 3.3, 3.4, 3.5
 *
 * - Exibe dropdown com lista de estabelecimentos vinculados ao lojista autenticado
 * - Desabilita dropdown quando há apenas 1 estabelecimento
 * - Pré-seleciona o estabelecimento ativo
 * - Exibe indicador de carregamento durante alternância
 * - Implementa retry (3 tentativas) com mensagem de erro e botão "Tentar novamente"
 */
const SeletorEstabelecimento: React.FC = () => {
  const { estabelecimentos, estabelecimentoAtual, carregando, erro, trocarEstabelecimento } =
    useEstabelecimento();

  const [tentativas, setTentativas] = useState(0);
  const ultimoIdSelecionado = useRef<string | null>(null);

  const possuiApenasUm = estabelecimentos.length <= 1;
  const erroAposTentativas = erro && tentativas >= MAX_TENTATIVAS;

  const aoSelecionarEstabelecimento = useCallback(
    (evento: React.ChangeEvent<HTMLSelectElement>) => {
      const novoId = evento.target.value;
      if (!novoId || novoId === estabelecimentoAtual?.id) return;

      ultimoIdSelecionado.current = novoId;
      setTentativas(1);
      trocarEstabelecimento(novoId);
    },
    [estabelecimentoAtual, trocarEstabelecimento]
  );

  const aoTentarNovamente = useCallback(() => {
    if (!ultimoIdSelecionado.current) return;

    setTentativas((prev) => prev + 1);
    trocarEstabelecimento(ultimoIdSelecionado.current);
  }, [trocarEstabelecimento]);

  // Se não há estabelecimentos, não renderiza
  if (estabelecimentos.length === 0) {
    return null;
  }

  return (
    <div className="seletor-estabelecimento">
      <label htmlFor="seletor-est" className="seletor-estabelecimento__label">
        Estabelecimento:
      </label>
      <div className="seletor-estabelecimento__wrapper">
        <select
          id="seletor-est"
          className="seletor-estabelecimento__select"
          value={estabelecimentoAtual?.id || ''}
          onChange={aoSelecionarEstabelecimento}
          disabled={possuiApenasUm || carregando}
          aria-label="Selecionar estabelecimento"
        >
          {estabelecimentos.map((est) => (
            <option key={est.id} value={est.id}>
              {est.nome}
            </option>
          ))}
        </select>

        {carregando && (
          <span className="seletor-estabelecimento__carregando" aria-live="polite">
            Alternando...
          </span>
        )}

        {erroAposTentativas && (
          <div className="seletor-estabelecimento__erro" role="alert">
            <span className="seletor-estabelecimento__erro-mensagem">
              Falha ao carregar dados do estabelecimento.
            </span>
            <button
              type="button"
              className="seletor-estabelecimento__btn-retry"
              onClick={aoTentarNovamente}
              aria-label="Tentar novamente"
            >
              Tentar novamente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeletorEstabelecimento;
